import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Knobs } from '../Docs/DocTypes';
import { KnobValues, parseCodeWithKnobs } from './CodePreview/KnobsUtils';
import { injectCode, getJSForEvents, removeEvents } from './RunningCodeUtils';
import { ErrorBoundary } from './ErrorBoundary';
import { transpileReactCode, isIE } from './ReactCodeTranspilation';
import { RunningCodeEventsRaised } from './RunningCodeEventsRaised';
import PhoneBorderImage from './phoneborder.png';

function unmountReactCode(parentElement: HTMLDivElement) {
  ReactDOM.unmountComponentAtNode(parentElement);
}

const codeClassName = 'code-preview-react-code';

type RunningReactCodeProps = {
  parentJSX: string;
  jsx?: string;
  knobs?: Knobs;
  knobValues: KnobValues;
  events?: string[];
  backgroundVisible?: boolean;
  isV3?: boolean;
  mobileView?: boolean;
};
export const RunningReactCode = (props: RunningReactCodeProps) => {
  const reactCodeElement = useRef<HTMLDivElement>(null);
  const ie = isIE();

  useEffect(() => {
    const reactCodeElementRef = reactCodeElement.current;
    if (reactCodeElement.current && props.jsx && !ie) {
      try {
        unmountReactCode(reactCodeElement.current);

        const compiledParentJS = props.parentJSX
          ? transpileReactCode(
              parseCodeWithKnobs(props.parentJSX, props.knobs, props.knobValues, false),
            )
          : '';
        const compiledJS = props.jsx
          ? transpileReactCode(parseCodeWithKnobs(props.jsx, props.knobs, props.knobValues, false))
          : '';

        const reactJS = `
        var domContainer = document.querySelector('#code-preview-react-code');
        function transformedJSX () {
          ${compiledParentJS}
          return ${compiledJS}
        }
        try {
          ReactDOM.render(transformedJSX(), domContainer);
        } catch (ex) {
          console.log('error running React code', ex);
        }`;
        injectCode(
          reactCodeElement.current,
          undefined,
          `
        ${reactJS}
        ${getJSForEvents(props.events, `.${codeClassName}`)}
          `,
        );
      } catch (ex) {}
    }
    return () => {
      if (reactCodeElementRef && props.jsx && !ie) {
        removeEvents(props.events, `.${codeClassName}`);
        unmountReactCode(reactCodeElementRef);
      }
    };
  }, [props.jsx, props.parentJSX, props.knobs, props.knobValues, ie, props.events]);

  if (ie) {
    return (
      <p>React preview not supported in IE. React code can't be compiled at runtime in IE ...</p>
    );
  }
  return (
    <ErrorBoundary>
      <div
        css={css`
          background-image: ${props.backgroundVisible
            ? `url('code-preview-background.png')`
            : undefined};
        `}
      >
        {props.mobileView && (
          <div
            className={`${codeClassName}  code-preview-html-code__mobile`}
            css={
              props.isV3 &&
              css`
                font-family: 'Brush Script MT', 'Brush Script Std', cursive;
              `
            }
            style={{
              width: '431px',
              height: '877px',
              background: `url('${PhoneBorderImage}') center center no-repeat`,
              backgroundSize: '431px 877px',
              boxSizing: 'border-box',
              padding: '105px 28px 105px 28px',
            }}
          >
            <div
              className={`code-preview-html-code__mobile--inner`}
              ref={reactCodeElement}
              id="code-preview-react-code"
              style={{
                height: '667px',
                width: '375px',
                transform: 'translate(0px, 0px)',
                overflow: 'hidden',
              }}
            ></div>
          </div>
        )}
        {!props.mobileView && (
          <div
            ref={reactCodeElement}
            className={`${codeClassName}`}
            id="code-preview-react-code"
            css={
              props.isV3 &&
              css`
                font-family: 'Brush Script MT', 'Brush Script Std', cursive;
              `
            }
          ></div>
        )}

        <RunningCodeEventsRaised htmlEvents={props.events} />
      </div>
    </ErrorBoundary>
  );
};
